import { useEffect, useRef } from 'react';
import { useUser } from '@/contexts/UserContext';
import { useAxiosWithAuth } from '@/hooks/useAxiosWithAuth';
import { useAppDispatch, useAppSelector } from '@/hooks/reduxHooks';
import {
  getProfileSettings,
  profileSettingsUpdatedEvent,
  type ProfileSettingsResponse
} from '@/feature/profile/api/profile.api';
import {
  setPanelInterfaceStyle,
  setPanelTheme
} from '@/store/slices/themeSlice';

export function PanelThemeSynchronizer() {
  const axiosInstance = useAxiosWithAuth();
  const dispatch = useAppDispatch();
  const panelTheme = useAppSelector((state) => state.theme);
  const { accessToken, user } = useUser();
  const themeSignatureRef = useRef('');

  themeSignatureRef.current = JSON.stringify({
    mode: panelTheme.mode,
    interfaceStyle: panelTheme.interfaceStyle,
    colors: panelTheme.customColors
  });

  useEffect(() => {
    if (!accessToken || !user) {
      return;
    }

    let isActive = true;
    const themeSignatureAtRequestStart = themeSignatureRef.current;

    const applySettings = (settings: ProfileSettingsResponse) => {
      dispatch(setPanelInterfaceStyle(settings.panelInterfaceStyle));
      dispatch(
        setPanelTheme({
          mode: settings.panelThemeMode,
          customColors: {
            primary: settings.panelPrimaryColor,
            background: settings.panelBackgroundColor,
            surface: settings.panelSurfaceColor,
            text: settings.panelTextColor,
            sidebar: settings.panelSidebarColor
          }
        })
      );
    };

    const handleSettingsUpdated = (event: Event) => {
      const settings = (event as CustomEvent<ProfileSettingsResponse>).detail;
      if (settings) {
        applySettings(settings);
      }
    };

    window.addEventListener(profileSettingsUpdatedEvent, handleSettingsUpdated);
    getProfileSettings(axiosInstance)
      .then((settings) => {
        if (
          isActive &&
          themeSignatureRef.current === themeSignatureAtRequestStart
        ) {
          applySettings(settings);
        }
      })
      .catch(() => {
        // Preferencje zapisane lokalnie pozostają aktywne, gdy profil jest chwilowo niedostępny.
      });

    return () => {
      isActive = false;
      window.removeEventListener(
        profileSettingsUpdatedEvent,
        handleSettingsUpdated
      );
    };
  }, [accessToken, axiosInstance, dispatch, user]);

  return null;
}
